
import { useState, useRef, useEffect } from 'react';

interface UseVideoPreviewOptions {
  isPreloadEnabled?: boolean;
  previewDelay?: number;
  autoplayOnLoad?: boolean;
}

export const useVideoPreview = (options: UseVideoPreviewOptions = {}) => {
  const { 
    isPreloadEnabled = true,
    previewDelay = 300,
    autoplayOnLoad = false
  } = options;

  const [isHovering, setIsHovering] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isPreloaded, setIsPreloaded] = useState(false);
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);
  const preloadTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isPreloadEnabled) {
      preloadTimerRef.current = setTimeout(() => {
        setIsPreloaded(true);
        // If autoplayOnLoad is enabled, show preview immediately after preload
        if (autoplayOnLoad) {
          setTimeout(() => {
            setShowPreview(true);
          }, 100);
        }
      }, 500);
    }

    return () => {
      if (preloadTimerRef.current) {
        clearTimeout(preloadTimerRef.current);
      }
    };
  }, [isPreloadEnabled, autoplayOnLoad]);

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    setIsHovering(true);
    if (isPreloaded) {
      hoverTimerRef.current = setTimeout(() => {
        setShowPreview(true);
      }, previewDelay);
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setShowPreview(false);
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  return {
    isHovering,
    showPreview,
    isPreloaded,
    handleMouseEnter,
    handleMouseLeave
  };
};
